import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { getAllQuestionnaireAnswers } from "@medplum/core";
import { QuestionnaireResponse } from "@medplum/fhirtypes";

import { Badge, Button, Group, Paper, Stack, Title } from "@mantine/core";

import { formatInTimeZone } from "date-fns-tz";

import AmendmentCardLabelText from "@/encounters/components/AmendmentCardLabelText";
import {
  EncounterAmendmentReason,
  EncounterAmendmentReasonLabels,
} from "@/encounters/constants/encounter-amendment-reason";
import { TransferOfCareEncounterLinkId } from "@/encounters/constants/transfer-of-care";

const TransferOfCareDiffHeader = ({
  questionnaireResponse,
}: {
  questionnaireResponse: QuestionnaireResponse;
}) => {
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const answers = getAllQuestionnaireAnswers(questionnaireResponse);
  const { encounterId, clientId } = useParams<{ encounterId: string; clientId: string }>();
  const navigate = useNavigate();
  const amendmentReason =
    answers[TransferOfCareEncounterLinkId.EncounterAmendmentInfoReason]?.[0]?.valueString;
  const amendmentNotes =
    answers[TransferOfCareEncounterLinkId.EncounterAmendmentInfoNotes]?.[0]?.valueString;
  const amendmentOtherSpecify =
    answers[TransferOfCareEncounterLinkId.EncounterAmendmentInfoSpecify]?.[0]?.valueString;

  const amendmentDate = formatInTimeZone(
    questionnaireResponse.meta?.lastUpdated ?? new Date(),
    userTimeZone,
    "MMMM d, yyyy · h:mm aa (zzz)",
  );

  const isOtherReason = amendmentReason === EncounterAmendmentReason.Other;

  const amendmentReasonText = useMemo(() => {
    const baseLabel =
      EncounterAmendmentReasonLabels[
        amendmentReason as keyof typeof EncounterAmendmentReasonLabels
      ] || "";

    if (isOtherReason && amendmentOtherSpecify) {
      return `${baseLabel} - ${amendmentOtherSpecify}`;
    }

    return baseLabel;
  }, [amendmentReason, amendmentOtherSpecify, isOtherReason]);

  return (
    <Paper p="md" withBorder>
      <Stack gap="xl">
        <Group justify="space-between">
          <Group gap="xs">
            <Title order={2}>Amendment</Title>
            <Badge c="blue.9" radius="xs" bg="blue.0">
              VIEWING AMENDMENT
            </Badge>
          </Group>
          <Button
            variant="outline"
            onClick={() => navigate(`/clients/${clientId}/encounters/${encounterId}`)}
          >
            Exit Amendment View
          </Button>
        </Group>
        <Group gap="xl">
          <AmendmentCardLabelText text={amendmentDate} label="Amendment Date" />
          <AmendmentCardLabelText
            label="Amended By"
            text={questionnaireResponse.meta?.author?.display ?? ""}
          />
          <AmendmentCardLabelText
            label="Signed & Lock by"
            text={questionnaireResponse.meta?.author?.display ?? ""}
          />
        </Group>
        <AmendmentCardLabelText label="Amendment Reason" text={amendmentReasonText ?? ""} />
        <AmendmentCardLabelText text={amendmentNotes} label="Amendment Note" />
      </Stack>
    </Paper>
  );
};

export default TransferOfCareDiffHeader;
